/**
 * Weights in pounds and ounces or in kilograms. Babia stores every weight in
 * whole grams (`entry.weight_grams`), the way it stores every bottle in
 * ounces, and each household picks the unit its parents hear at the clinic
 * (`household.weight_unit`). Ounces show to a tenth, as a clinic's scale
 * reads, and whole grams hold every tenth of an ounce exactly enough to read
 * it back.
 */

export const WEIGHT_UNITS = ["lb", "kg"] as const;
export type WeightUnit = (typeof WEIGHT_UNITS)[number];

/** The unit as a choice to pick in Settings. */
export const WEIGHT_UNIT_LABEL: Record<WeightUnit, string> = {
  lb: "Pounds and ounces (lb oz)",
  kg: "Kilograms (kg)",
};

/** What a weight in each unit is called. */
export const WEIGHT_UNIT_NAME: Record<WeightUnit, string> = {
  lb: "pounds and ounces",
  kg: "kilograms",
};

/** Grams in an avoirdupois ounce. */
export const GRAMS_PER_OZ = 28.349523125;

/** The lightest and heaviest weight Babia takes, in grams. */
export const MIN_WEIGHT_GRAMS = 300;
export const MAX_WEIGHT_GRAMS = 30_000;

/** Whole grams in `pounds` and `ounces`. */
export function gramsFromPounds(pounds: number, ounces: number): number {
  return Math.round((pounds * 16 + ounces) * GRAMS_PER_OZ);
}

/** Whole grams in `kg` kilograms. */
export function gramsFromKilograms(kg: number): number {
  return Math.round(Number((kg * 1000).toPrecision(12)));
}

/** Whole pounds and ounces to a tenth: 15.96 oz carries to the next pound. */
export function poundsAndOunces(grams: number): { pounds: number; ounces: number } {
  const tenths = Math.round((grams / GRAMS_PER_OZ) * 10);
  return { pounds: Math.floor(tenths / 160), ounces: (tenths % 160) / 10 };
}

/** "9 lb 4.2 oz", "9 lb 0 oz", "4.21 kg", "4 kg". */
export function formatWeight(grams: number, unit: WeightUnit): string {
  if (unit === "kg") return `${Number((grams / 1000).toFixed(3))} kg`;
  const { pounds, ounces } = poundsAndOunces(grams);
  return `${pounds} lb ${ounces} oz`;
}

/** Why Babia refuses a weight, or null to take it. */
export function weightProblem(grams: number): string | null {
  if (!Number.isFinite(grams)) return "a weight must be a number";
  if (grams < MIN_WEIGHT_GRAMS || grams > MAX_WEIGHT_GRAMS) {
    return "a weight must be between 0.3 kg and 30 kg (about 11 oz to 66 lb)";
  }
  return null;
}
